import { throttle } from 'lodash-es';
import { useEffect, useRef } from 'react';

type Options = {
  disabled?: boolean;
  margin?: number;
  initialTop?: number;
};

export function useDynamicSticky(options: Options = {}) {
  const { disabled = false, margin = 0, initialTop: initialTopOption } = options;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || disabled) return;

    const element = ref.current;
    let initialTop = initialTopOption ?? element.getBoundingClientRect().top + window.pageYOffset;

    let lastPos = window.scrollY;
    let prvTranslateAmount = 0;
    let translateAmount = 0;
    const HEIGHT = element.clientHeight - margin;

    let rafId: number | null = null;

    const updateInitialTop = throttle(
      () => {
        if (!element) return;
        initialTop = element.getBoundingClientRect().top + window.pageYOffset;
      },
      300,
      { leading: true },
    );

    const handleScroll = () => {
      const curPos = window.scrollY;
      const delta = curPos - lastPos;
      lastPos = curPos;
      updateInitialTop();

      if (curPos < initialTop) {
        translateAmount = 0;
      } else {
        if (delta > 0) {
          translateAmount = Math.max(translateAmount - delta, -HEIGHT);
        } else {
          translateAmount = Math.min(translateAmount - delta, 0);
        }
      }

      if (!element || rafId) return;
      if (prvTranslateAmount === translateAmount) return;
      prvTranslateAmount = translateAmount;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!element) return;
        element.style.transform = `translateY(${translateAmount}px)`;
      });
    };

    document.addEventListener('scroll', handleScroll);

    return () => {
      element.style.transform = `translateY(0px)`;
      document.removeEventListener('scroll', handleScroll);
    };
  }, [disabled, margin]);

  return ref;
}
